import { useTranslation } from 'react-i18next';

import type { Member, MemberStatus } from '@nucleus-commerce/api-client/admin';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Badge } from '@nucleus-commerce/ui/components/badge';
import { Tooltip, TooltipContent, TooltipTrigger } from '@nucleus-commerce/ui/components/tooltip';
import { cn } from '@nucleus-commerce/ui/lib/utils';

const STYLES: Record<MemberStatus, string> = {
  active: 'bg-success/15 text-success border-transparent dark:bg-success/20',
  invited: 'bg-warning/20 text-warning-foreground border-transparent dark:text-warning',
  removed: 'border-border text-muted-foreground',
};

/** Status pill; hovering shows when the invitation went out and when it was accepted. */
export function MemberStatusBadge({ member }: { member: Member }) {
  const { t } = useTranslation('team');
  const format = useFormat();
  const lines = [
    member.invitedAt ? t('status.invitedAt', { when: format.dateTime(member.invitedAt) }) : null,
    member.acceptedAt
      ? t('status.acceptedAt', { when: format.dateTime(member.acceptedAt) })
      : member.status === 'invited'
        ? t('status.pending')
        : null,
  ].filter((line): line is string => line !== null);

  const badge = (
    <Badge variant="outline" className={cn(STYLES[member.status])}>
      {t(`status.${member.status}`)}
    </Badge>
  );
  if (lines.length === 0) return badge;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button type="button" className="inline-flex cursor-default rounded-full">
          {badge}
        </button>
      </TooltipTrigger>
      <TooltipContent>
        {lines.map((line) => (
          <p key={line}>{line}</p>
        ))}
      </TooltipContent>
    </Tooltip>
  );
}
